import { ActionContext, ActionTree } from "vuex";
import download from "downloadjs";
import { StatusCodes } from "http-status-codes";

import {
  ICoreErrorResponse,
  ICoreResponse,
} from "@/core/interfaces/coreResponse.interface";

import { IFlightBooking } from "@/core/interfaces/IFlightBooking.interface";

import FlightBookingService from "@/modules/Booking/services/booking.service";
import {
  GetBookingPayloadType,
  GetDownloadBookingPayloadType,
  ICancelBookingPayloadType,
  IConfirmBooking,
  IInitiateVoidBookingPayloadType,
  ICommitVoidBookingPayloadType,
  Traveler,
} from "@/core/types/flight/FlightBookingForm";
import { BookingFormTravelerData } from "@/core/constants/bookingForm";
import { TravelerTypes } from "@/core/constants/TravelerTypes";
import notificationService from "@/core/services/notification.service";
import { NOTIFICATION_TYPES } from "@/core/enums/NOTIFICATION_TYPES";
import { CACHE_NAMES } from "@/core/constants";
import { IPassenger } from "@/core/interfaces/passenger.interface";
import { NOTIFICATION_MESSAGES } from "@/core/constants/notificationMessages";
import { StoreState } from "@/store/type";
import { BookingErrorMessage, ConfirmAddOns } from "../constants";
import {
  AdditionalPassengerField,
  BookingAlert,
  EditPassengerApiErrors,
  EditPassengerRequest,
} from "@/core/types/flight";
import { extractMarketingAirlineCodes } from "@/core/utils/flight";

export interface SendAirlineTicketBodyRequest {
  pnr: string;
  airline_provider: string;
  emails: string[];
}

const actions: ActionTree<IFlightBooking, StoreState> = {
  async sendAirlineTicket(
    context: ActionContext<IFlightBooking, StoreState>,
    {
      payload,
      callback,
    }: { payload: SendAirlineTicketBodyRequest; callback: () => void },
  ) {
    context.commit("enableIsSendingAirlineTicket");

    const myBookingsService = new FlightBookingService();

    await myBookingsService.sendAirlineTicket(payload);

    context.commit("disableIsSendingAirlineTicket");

    callback();
  },

  // **** Get Flight Booking ****
  async getFlightBooking(
    context: ActionContext<IFlightBooking, StoreState>,
    payload: GetBookingPayloadType,
  ) {
    try {
      context.commit("setLoading", true);

      const flightBooking = new FlightBookingService();
      const response: ICoreResponse = await flightBooking.getBooking(payload);

      if (response.success && response.status === StatusCodes.OK) {
        const flightDetailResponse = response.data?.data || {};
        const bookingId = flightDetailResponse.booking_id;
        const flightDetails = flightDetailResponse.flight_details;
        const additionalPassengerFields: AdditionalPassengerField[] | null =
          response?.data?.data?.additional_passenger_fields;

        const alerts: BookingAlert[] | null = response?.data?.data?.alerts;
        context.commit("saveBookingId", bookingId);
        context.commit("saveBookingStatus", flightDetailResponse?.status);
        context.commit("saveAddonsMeta", {
          isAddonsEnabled: Boolean(flightDetailResponse?.is_addons_enabled),
          addonsStatus: (
            flightDetailResponse?.addons_status || ""
          ).toUpperCase(),
        });
        context.commit(
          "saveBookingActions",
          flightDetailResponse.booking_actions,
        );
        context.commit(
          "saveBookingFinancialProfile",
          response.data?.data?.financial_profile,
        );
        context.commit(
          "saveBookingPromoDealDetails",
          flightDetailResponse.booking_promo_deal_details,
        );
        context.commit(
          "savePnrExpiry",
          flightDetailResponse.confirmed_booking_expires_at,
        );
        context.commit(
          "saveBookingOrganizationUser",
          flightDetailResponse.organization_user,
        );
        context.commit("saveBookingCreatedBy", flightDetailResponse.created_by);
        context.commit("saveFlightDetails", flightDetails);
        context.commit(
          "saveMarketingAirlineCodes",
          extractMarketingAirlineCodes(flightDetails.flight_legs ?? []),
        );
        context.commit(
          "saveAdditionalPassengerFields",
          additionalPassengerFields,
        );
        context.commit(
          "saveOrganizationName",
          flightDetailResponse.organization_name,
        );
        context.commit("savePlatformId", flightDetailResponse.platform_id);
        context.commit("saveBookingAlerts", alerts);
        context.commit(
          "saveApprovalRequest",
          flightDetailResponse.approval_request ?? null,
        );
        context.commit(
          "saveFrequentFlyersAllowed",
          Boolean(flightDetailResponse.frequent_flyers_allowed),
        );
        context.commit(
          "saveBookingOrganizationId",
          flightDetailResponse.organization_id ?? null,
        );
        const travelerFormItems = [
          ...Array.from(Array(flightDetails.adult_count).keys()).map(() => ({
            ...BookingFormTravelerData,
            is_international: flightDetails?.is_international,
            passenger_type: TravelerTypes.ADULT,
            nationality: "PK",
          })),
          ...Array.from(Array(flightDetails.child_count).keys()).map(() => ({
            ...BookingFormTravelerData,
            is_international: flightDetails?.is_international,
            passenger_type: TravelerTypes.CHILD,
            nationality: "PK",
          })),
          ...Array.from(Array(flightDetails.infant_count).keys()).map(() => ({
            ...BookingFormTravelerData,
            is_international: flightDetails?.is_international,
            passenger_type: TravelerTypes.INFANT,
            nationality: "PK",
          })),
        ];

        context.commit("saveTravelers", travelerFormItems);
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    } finally {
      context.commit("setLoading", false);
    }
  },

  // **** Get Flight Booking ****
  async getAllAddOns(
    context: ActionContext<IFlightBooking, StoreState>,
    payload: string,
  ) {
    try {
      context.commit("setAddOnsLoading", true);

      const flightBooking = new FlightBookingService();
      const response: ICoreResponse = await flightBooking.getAllAddOns(payload);

      if (response.success && response.status === StatusCodes.OK) {
        const raw = response.data;
        const addOnsResponse = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.data)
            ? raw.data
            : Array.isArray(raw?.response)
              ? raw.response
              : [];

        context.commit("saveAddOns", addOnsResponse);
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    } finally {
      context.commit("setAddOnsLoading", false);
    }
  },

  // **** Get Flight Booking ****
  async saveAllAddOns(
    context: ActionContext<IFlightBooking, StoreState>,
    payload: ConfirmAddOns,
  ) {
    try {
      context.commit("setAddOnsLoading", true);

      const flightBooking = new FlightBookingService();
      const response: ICoreResponse =
        await flightBooking.saveAllAddOns(payload);

      if (response.success && response.status === StatusCodes.OK) {
        window.location.reload();
      } else {
        context.commit("resetAddOns");
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    } finally {
      context.commit("setAddOnsLoading", false);
    }
  },

  async skipAddOns(
    context: ActionContext<IFlightBooking, StoreState>,
    payload: ConfirmAddOns,
  ) {
    try {
      context.commit("setAddOnsLoading", true);
      context.commit("setSkipAddOnsLoading", true);
      const flightBooking = new FlightBookingService();
      const getAddOnsResponse: ICoreResponse = await flightBooking.getAllAddOns(
        payload.booking_id,
      );

      if (
        getAddOnsResponse.success &&
        getAddOnsResponse.status === StatusCodes.OK
      ) {
        const saveResponse: ICoreResponse =
          await flightBooking.saveAllAddOns(payload);

        if (saveResponse.success && saveResponse.status === StatusCodes.OK) {
          window.location.reload();
        } else {
          throw saveResponse;
        }
      } else {
        throw getAddOnsResponse;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    } finally {
      context.commit("setAddOnsLoading", false);
      context.commit("setSkipAddOnsLoading", false);
    }
  },

  // **** Confirm Flight Booking ****
  async confirmFlightBooking(
    context: ActionContext<IFlightBooking, StoreState>,
    {
      payload,
      successCallback,
      errorCallback,
    }: {
      payload: IConfirmBooking;
      successCallback: (booking_id: string) => void;
      errorCallback: () => void;
    },
  ) {
    try {
      context.commit("setConfirmBookingLoading", true);

      context.commit("setBookingDetailError", null);
      const flightBooking = new FlightBookingService();
      const response = await flightBooking.confirmBooking(payload);

      if (response.success && response.status === StatusCodes.OK) {
        const booking_id = response?.data?.data?.booking_id;

        await caches.delete(CACHE_NAMES.flightsBookings);

        successCallback(booking_id);
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
      context.commit("setBookingDetailError", exception?.errors);

      if (exception.message && exception.message === BookingErrorMessage) {
        errorCallback();
      }
    } finally {
      context.commit("setConfirmBookingLoading", false);
    }
  },

  // **** Cancel Flight Booking ****
  async cancelFlightBooking(
    context: ActionContext<IFlightBooking, StoreState>,
    {
      payload,
      successCallback,
    }: {
      payload: ICancelBookingPayloadType;
      successCallback: () => void;
    },
  ) {
    try {
      context.commit("setCancelBookingLoading", true);

      const flightBooking = new FlightBookingService();
      const response: ICoreResponse =
        await flightBooking.cancelBooking(payload);

      if (response.success && response.status === StatusCodes.OK) {
        await caches.delete(CACHE_NAMES.flightsBookings);

        successCallback();
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    } finally {
      context.commit("setCancelBookingLoading", false);
    }
  },

  // **** Cancel Flight Booking ****
  async downloadFlightBooking(
    context: ActionContext<IFlightBooking, StoreState>,
    payload: GetDownloadBookingPayloadType,
  ) {
    try {
      context.commit("setDownloadTicketLoading", true);

      const flightBooking = new FlightBookingService();
      const response: ICoreResponse =
        await flightBooking.downloadFlightTicket(payload);

      if (response.success && response.status === StatusCodes.OK) {
        download(response.data, payload.pnr + "-E-ticket.pdf");
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    } finally {
      context.commit("setDownloadTicketLoading", false);
    }
  },
  // **** InitiateVoid Flight Booking ****
  async initiateVoidFlightBooking(
    context: ActionContext<IFlightBooking, StoreState>,
    payload: IInitiateVoidBookingPayloadType,
  ) {
    try {
      context.commit("setVoidTicketLoading", true);

      const flightBooking = new FlightBookingService();
      const response: ICoreResponse =
        await flightBooking.initiateVoidFlightBooking(payload);

      if (response.success && response.status === StatusCodes.OK) {
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description = response.message?.toString() || "";
        notificationService.triggerNotification();
        return response.data?.data?.reference as string;
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    } finally {
      context.commit("setVoidTicketLoading", false);
    }
  },

  // **** Void Flight Booking ****
  async voidFlightBooking(
    context: ActionContext<IFlightBooking, StoreState>,
    {
      payload,
      successCallback,
    }: {
      payload: ICommitVoidBookingPayloadType;
      successCallback: () => void;
    },
  ) {
    try {
      context.commit("setVoidTicketLoading", true);

      const flightBooking = new FlightBookingService();
      const response: ICoreResponse =
        await flightBooking.voidFlightBooking(payload);

      if (response.success && response.status === StatusCodes.OK) {
        await caches.delete(CACHE_NAMES.flightsBookings);

        successCallback();

        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description = response.message?.toString() || "";
        notificationService.triggerNotification();
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    } finally {
      context.commit("setVoidTicketLoading", false);
    }
  },

  // **** Get Organization Passengers ****
  async getOrganizationPassengers(
    context: ActionContext<IFlightBooking, StoreState>,
    {
      organizationId,
      passengerIds,
    }: { organizationId: string; passengerIds?: string[] },
  ) {
    try {
      context.commit("setPassengerLoading", true);

      const flightBooking = new FlightBookingService();
      const response: ICoreResponse =
        await flightBooking.getOrganizationPassengers(
          organizationId,
          passengerIds,
        );

      if (response.success && response.status === StatusCodes.OK) {
        context.commit("setPassengerLoading", false);

        const passengers: IPassenger[] = response.data;
        const updatedPassengers = passengers.map((x) => {
          return {
            ...x,
            fullName: x?.first_name + " " + x?.last_name,
          };
        });

        context.commit("setPassengers", updatedPassengers);
      } else {
        context.commit("setPassengerLoading", false);
        throw response;
      }
    } catch (exception) {
      context.commit("setPassengerLoading", false);
      console.error("Error while getting organization passengers:", exception);
    }
  },

  async createPassengers(
    context: ActionContext<IFlightBooking, StoreState>,
    {
      body,
      organizationId,
    }: { request_source: string; body: Traveler[]; organizationId: string },
  ) {
    try {
      const flightBooking = new FlightBookingService();
      const response: ICoreResponse = await flightBooking.createPassengers(
        body,
        organizationId,
      );

      if (
        response.success &&
        (response.status === StatusCodes.CREATED ||
          response.status === StatusCodes.OK)
      ) {
        // Success case - no additional action needed
      } else {
        throw response;
      }
    } catch (exception) {
      console.error("Error in createPassengers:", exception);
    }
  },

  async editPassengersDetail(
    context: ActionContext<IFlightBooking, StoreState>,
    { body, bookingId }: { body: EditPassengerRequest; bookingId: string },
  ) {
    context.commit("setEditPassengerApiErrors", null);

    try {
      context.commit("setEditingPassenger", true);

      const flightBooking = new FlightBookingService();
      const response: ICoreResponse = await flightBooking.editPassengersDetail(
        body,
        bookingId,
      );

      if (response.success && response.status === StatusCodes.OK) {
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          "Successfully updated passenger details";
        notificationService.triggerNotification();
        return true;
      } else {
        context.commit(
          "setEditPassengerApiErrors",
          response.errors as EditPassengerApiErrors,
        );
        context.commit("setEditingPassenger", false);
        return false;
      }
    } catch (exception: unknown) {
      const error = exception as ICoreErrorResponse;

      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        error.message || error.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
      console.error("Error in editPassengerDetails:", error);
    } finally {
      context.commit("setEditingPassenger", false);
    }
  },
};

export default actions;
